/**
 * Self-hosted Capgo OTA (GroupKitty pattern).
 *
 * - Plugin autoUpdate is OFF; empty Capgo Cloud URLs.
 * - App fetches manifest from Firebase Hosting:
 *     staging    → {VITE_OTA_BASE_URL}/ota/{tenant}/staging/latest.json
 *     production → {VITE_OTA_BASE_URL}/ota/{tenant}/latest.json
 * - Downloads zip + checksum via CapacitorUpdater; prompts user to restart.
 * - Production pointer advances only via promote (public/ota/<tenant>/live.json).
 */
import { loadOtaInfo } from './otaInfo';

export type OtaManifest = {
  version: string;
  url: string;
  checksum: string;
  builtAt?: string;
  otaVersion?: number;
};

export type OtaUpdateReady = {
  version: string;
  bundleId: string;
};

type BundleInfo = {
  id: string;
  version: string;
  status?: string;
};

const OTA_CHANNEL =
  String(import.meta.env.VITE_OTA_CHANNEL || '')
    .trim()
    .toLowerCase() === 'staging'
    ? 'staging'
    : 'production';

const TENANT = String(import.meta.env.VITE_TENANT_SLUG || 'vipso')
  .trim()
  .toLowerCase();

const OTA_BASE = String(import.meta.env.VITE_OTA_BASE_URL || '')
  .trim()
  .replace(/\/$/, '');

const LATEST_URL = OTA_BASE
  ? OTA_CHANNEL === 'staging'
    ? `${OTA_BASE}/ota/${TENANT}/staging/latest.json`
    : `${OTA_BASE}/ota/${TENANT}/latest.json`
  : '';

const OTA_DISMISS_PREFIX = 'vipso_ota_dismissed_';

type ReadyListener = (update: OtaUpdateReady) => void;
let readyListener: ReadyListener | null = null;
let pendingReady: OtaUpdateReady | null = null;

export function onOtaUpdateReady(listener: ReadyListener | null): void {
  readyListener = listener;
  if (listener && pendingReady) {
    listener(pendingReady);
  }
}

function emitReady(update: OtaUpdateReady): void {
  pendingReady = update;
  readyListener?.(update);
}

function versionRank(v: string): number {
  if (!v || v === 'builtin') return -1;
  const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(v.trim());
  if (!m) return -1;
  return Number(m[1]) * 1_000_000 + Number(m[2]) * 1_000 + Number(m[3]);
}

function isOtaDismissed(version: string): boolean {
  try {
    return localStorage.getItem(`${OTA_DISMISS_PREFIX}${version}`) === '1';
  } catch {
    return false;
  }
}

export function dismissOtaUpdate(version: string): void {
  try {
    localStorage.setItem(`${OTA_DISMISS_PREFIX}${version}`, '1');
  } catch {
    // ignore
  }
}

async function runningSemver(
  CapacitorUpdater: typeof import('@capgo/capacitor-updater').CapacitorUpdater,
): Promise<string> {
  const current = await CapacitorUpdater.current();
  if (current.bundle.id !== 'builtin') {
    return current.bundle.version;
  }
  const local = await loadOtaInfo();
  return `1.0.${local?.version ?? 0}`;
}

async function fetchLatest(): Promise<OtaManifest | null> {
  if (!LATEST_URL) {
    console.warn('[ota] VITE_OTA_BASE_URL is not set — skipping live updates');
    return null;
  }
  try {
    const res = await fetch(`${LATEST_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) {
      console.warn(`[ota] manifest HTTP ${res.status} from ${LATEST_URL}`);
      return null;
    }
    const data = (await res.json()) as Partial<OtaManifest>;
    if (!data.version || !data.url || !data.checksum) {
      console.warn('[ota] manifest missing version/url/checksum', data);
      return null;
    }
    return {
      version: String(data.version),
      url: String(data.url),
      checksum: String(data.checksum),
      builtAt: data.builtAt,
      otaVersion: data.otaVersion,
    };
  } catch (err) {
    console.warn('[ota] manifest fetch failed', err);
    return null;
  }
}

async function findLocalBundle(
  CapacitorUpdater: typeof import('@capgo/capacitor-updater').CapacitorUpdater,
  version: string,
): Promise<BundleInfo | null> {
  try {
    const pending = await CapacitorUpdater.getNextBundle();
    if (pending && pending.version === version) return pending;

    const { bundles } = await CapacitorUpdater.list();
    const match = bundles.find(
      (b) => b.version === version && b.id !== 'builtin' && b.status !== 'error',
    );
    return match ?? null;
  } catch {
    return null;
  }
}

/** Apply a downloaded bundle immediately (destroys JS context — app reloads). */
export async function applyOtaUpdate(bundleId: string): Promise<void> {
  const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
  await CapacitorUpdater.set({ id: bundleId });
}

/**
 * Self-hosted Capgo OTA. No-ops on web or when VITE_OTA_BASE_URL is unset.
 */
export async function initLiveUpdates(): Promise<void> {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return;
    if (!OTA_BASE) {
      console.info('[ota] skipped — set VITE_OTA_BASE_URL for self-hosted updates');
      return;
    }

    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    await CapacitorUpdater.notifyAppReady();
    console.info(`[ota] channel=${OTA_CHANNEL} tenant=${TENANT} manifest=${LATEST_URL}`);

    const notifyReady = (bundle: BundleInfo, version: string) => {
      if (isOtaDismissed(version)) {
        console.info(`[ota] ${version} ready but user dismissed restart prompt`);
        return;
      }
      emitReady({ version, bundleId: bundle.id });
    };

    const checkAndDownload = async () => {
      try {
        const latest = await fetchLatest();
        if (!latest) {
          console.info(`[ota] no ${OTA_CHANNEL} update published yet`);
          return;
        }

        const running = await runningSemver(CapacitorUpdater);
        const current = await CapacitorUpdater.current();
        console.info(
          `[ota] running=${running} (bundle=${current.bundle.id}) latest=${latest.version}`,
        );

        if (versionRank(latest.version) <= versionRank(running)) {
          console.info(`[ota] up to date (${running})`);
          return;
        }

        const existing = await findLocalBundle(CapacitorUpdater, latest.version);
        if (existing) {
          console.info(`[ota] ${latest.version} already downloaded — prompting restart`);
          await CapacitorUpdater.next({ id: existing.id });
          notifyReady(existing, latest.version);
          return;
        }

        console.info(`[ota] downloading ${latest.version} (have ${running})`);
        const bundle = await CapacitorUpdater.download({
          url: latest.url,
          version: latest.version,
          checksum: latest.checksum,
        });
        await CapacitorUpdater.next({ id: bundle.id });
        console.info(`[ota] ${latest.version} downloaded — prompting restart`);
        notifyReady(bundle, latest.version);
      } catch (err) {
        console.warn('[ota] live update check failed', err);
      }
    };

    void checkAndDownload();

    try {
      const { App } = await import('@capacitor/app');
      await App.addListener('appStateChange', (state) => {
        if (state.isActive) void checkAndDownload();
      });
    } catch {
      // @capacitor/app optional — startup check still runs
    }
  } catch (err) {
    console.warn('[ota] init skipped', err);
  }
}
